import React, { useEffect, useState, useCallback } from 'react';
import { FiCheckCircle, FiDownload, FiX, FiArrowRight, FiAlertTriangle } from 'react-icons/fi';
import paymentService from '../../services/payment.service';

const PaymentSuccessModal = ({ 
  isOpen, 
  onClose, 
  paymentId,
  onNavigate 
}) => {
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // States to orchestrate the extended closing animation smoothly (500ms)
  const [isRendered, setIsRendered] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  // 1. Wrap handleDelayedClose in useCallback so its reference is stable
  const handleDelayedClose = useCallback(() => {
    setAnimateOut(true);
    setTimeout(() => {
      onClose();
    }, 500);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      setAnimateOut(false);
      if (paymentId) {
        fetchPaymentStatus(paymentId);
      }
    } else {
      // Trigger out-animation when parent says close
      setAnimateOut(true);
      const timer = setTimeout(() => setIsRendered(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, paymentId]);

  const fetchPaymentStatus = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await paymentService.getPaymentStatus(id);
      if (response && response.success) {
        setPaymentData(response.data);
      } else {
        setError(response?.message || 'We couldn\'t verify your payment details.');
      }
    } catch (err) {
      setError(err.message || 'A network error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 2. Added handleDelayedClose safely to the dependency array here
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) handleDelayedClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleDelayedClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleDelayedClose();
  };

  if (!isRendered) return null;

  return (
    <div 
      className={`fixed inset-0 bg-slate-950/40 backdrop-blur-md flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-opacity duration-500 ease-out ${
        animateOut ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleOverlayClick}
    >
      <div 
        className={`bg-white rounded-t-[2.5rem] sm:rounded-2xl max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto transform transition-all duration-500 ease-out flex flex-col border border-slate-100 ${
          animateOut 
            ? 'translate-y-full sm:translate-y-4 sm:scale-95 sm:opacity-0' 
            : 'translate-y-0 sm:scale-100 sm:opacity-100'
        }`}
      >
        {/* Top bar / Close actions */}
        <div className="flex items-center justify-between pt-5 px-6">
          <div className="w-12 h-1 bg-slate-200 rounded-full mx-auto sm:hidden abs left-1/2 -translate-x-1/2 top-3 absolute" />
          <div className="w-4 h-4 hidden sm:block" /> 
          <button 
            onClick={handleDelayedClose}
            className="p-2 hover:bg-slate-100 active:bg-slate-200 rounded-full transition-all duration-200 ml-auto focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            aria-label="Close modal"
          >
            <FiX className="w-5 h-5 text-slate-400 hover:text-slate-600 transition-colors" />
          </button>
        </div>

        <div className="px-6 pb-8 pt-2 flex-1">
          {loading ? (
            /* --- Premium Loading State --- */
            <div className="text-center py-16">
              <div className="relative w-12 h-12 mx-auto mb-5">
                <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-20" />
                <div className="relative rounded-full h-12 w-12 border-[3.5px] border-indigo-600 border-t-transparent animate-spin" />
              </div>
              <h3 className="text-base font-semibold text-slate-800 mb-1">Verifying Secure Payment</h3>
              <p className="text-xs text-slate-400">Please do not refresh or close this screen</p>
            </div>
          ) : error ? (
            /* --- Premium Error State --- */
            <div className="text-center py-6">
              <div className="relative w-16 h-16 mx-auto mb-5 flex items-center justify-center">
                <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-15" />
                <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center relative border border-rose-100">
                  <FiAlertTriangle className="w-7 h-7 text-rose-500" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Verification Failed</h2>
              <p className="text-sm text-slate-500 max-w-xs mx-auto mb-8 leading-relaxed">{error}</p>
              
              <button
                onClick={handleDelayedClose}
                className="w-full py-3.5 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 active:scale-[0.98] transition-all shadow-sm text-sm"
              >
                Close Window
              </button>
            </div>
          ) : (
            /* --- Premium Success State --- */
            <>
              {/* Success Hero Header */}
              <div className="text-center mb-8">
                <div className="relative w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <div className="animate-ping absolute inline-flex h-[80%] w-[80%] rounded-full bg-emerald-400 opacity-20" />
                  <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center relative border border-emerald-100">
                    <FiCheckCircle className="w-8 h-8 text-emerald-500" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">
                  Payment Successful
                </h2>
                <p className="text-sm text-slate-400 max-w-xs mx-auto">
                  Your transaction went through cleanly. Wallet funds have been updated.
                </p>
              </div>

              {/* Main Minimalist Receipt Card */}
              {paymentData && (
                <div className="bg-slate-50/60 border border-slate-100/80 rounded-2xl p-6 mb-8 space-y-4">
                  <div className="text-center pb-4 border-b border-slate-200/50">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">
                      Total Amount Paid
                    </span>
                    <span className="text-3xl font-black text-slate-900 tracking-tight">
                      ₹{paymentData.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                    </span>
                  </div>

                  <div className="space-y-3 pt-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 font-medium">Receipt ID</span>
                      <span className="font-mono font-bold text-slate-700 bg-white px-2.5 py-1 border border-slate-200/60 rounded-md text-xs shadow-xs">
                        {paymentData.id?.slice(-8).toUpperCase() || 'N/A'}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 font-medium">Method</span>
                      <span className="font-semibold text-slate-700 capitalize">
                        {paymentData.paymentType || 'N/A'}
                      </span>
                    </div>

                    {paymentData.bundleName && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 font-medium">Package</span>
                        <span className="font-semibold text-slate-700 truncate max-w-[180px]">
                          {paymentData.bundleName}
                        </span>
                      </div>
                    )}

                    {paymentData.credits > 0 && (
                      <div className="flex justify-between items-center pt-3 border-t border-dashed border-slate-200">
                        <span className="font-semibold text-slate-800">Credits Added</span>
                        <span className="font-extrabold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-xs">
                          +{paymentData.credits}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Interactive Actions Grid */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    handleDelayedClose();
                    if (onNavigate) onNavigate('/communication');
                  }}
                  className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-indigo-800 active:scale-[0.99] transition-all flex items-center justify-center gap-2 text-sm shadow-md shadow-indigo-600/15 group"
                >
                  Continue to Dashboard
                  <FiArrowRight className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" />
                </button>
                
                <button
                  onClick={() => console.log('Download receipt')}
                  className="w-full py-3.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 active:scale-[0.99] transition-all flex items-center justify-center gap-2 text-sm"
                >
                  <FiDownload className="w-4 h-4 text-slate-400" />
                  Download Invoice PDF
                </button>

                <button
                  onClick={handleDelayedClose}
                  className="w-full py-2 text-slate-400 font-semibold hover:text-slate-500 text-xs transition-colors mt-2 block sm:hidden text-center"
                >
                  Dismiss Overlay
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessModal;